import { useRef, useState } from 'react'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

interface PinInputProps {
  length?: number
  onComplete: (pin: string) => void
  disabled?: boolean
  error?: boolean
  className?: string
}

export function PinInput({
  length = 4,
  onComplete,
  disabled = false,
  error = false,
  className,
}: PinInputProps) {
  const [values, setValues] = useState<string[]>(Array(length).fill(''))
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  const handleChange = (index: number, value: string) => {
    if (!/^\d?$/.test(value)) return

    const newValues = [...values]
    newValues[index] = value
    setValues(newValues)

    if (value && index < length - 1) {
      inputRefs.current[index + 1]?.focus()
    }

    if (value && index === length - 1) {
      const pin = newValues.join('')
      if (pin.length === length) {
        onComplete(pin)
        // Reset after a short delay for visual feedback
        setTimeout(() => {
          setValues(Array(length).fill(''))
          inputRefs.current[0]?.focus()
        }, 300)
      }
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !values[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length)
    if (pasted.length === length) {
      const newValues = pasted.split('')
      setValues(newValues)
      onComplete(pasted)
      setTimeout(() => {
        setValues(Array(length).fill(''))
        inputRefs.current[0]?.focus()
      }, 300)
    }
  }

  return (
    <div className={cn('flex gap-2 justify-center', className)}>
      {values.map((value, index) => (
        <Input
          key={index}
          ref={(el) => { inputRefs.current[index] = el }}
          type="password"
          inputMode="numeric"
          maxLength={1}
          value={value}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          disabled={disabled}
          className={cn(
            'w-12 h-12 text-center text-lg font-mono',
            error && 'border-destructive focus-visible:ring-destructive animate-shake'
          )}
          autoFocus={index === 0}
        />
      ))}
    </div>
  )
}
